import { ElementManager } from '../../../base/elements';
import { listen, vdsEvent } from '../../../base/events';
import { DEV_MODE } from '../../../global/env';
import { mediaContext } from '../../context';
import { ManagedControlsConnectEvent } from './ManagedControls';
/**
 * A registry for all media controls that:
 *
 * - Listens for new controls connecting in the DOM and adds them to the registry.
 * - Manages showing and hiding all controls in-sync.
 * - Listens for relevant requests such as `ShowControlsRequestEvent` and handles them.
 * - Updates `mediaContext.customControls`.
 */
export class ControlsManager extends ElementManager {
    constructor(_host) {
        super(_host);
        this._host = _host;
        this._prevHiddenValue = mediaContext.customControls.initialValue;
    }
    static get _ScopedDiscoveryEvent() {
        return ManagedControlsConnectEvent;
    }
    /**
     * Whether controls are currently hidden.
     */
    get isHidden() {
        return !this._host.controller.mediaCtx.customControls;
    }
    _handleHostConnected() {
        super._handleHostConnected();
        this._disconnectDisposal.add(listen(this._host, 'vds-show-controls-request', this._handleShowControlsRequest.bind(this)));
        this._disconnectDisposal.add(listen(this._host, 'vds-hide-controls-request', this._handleHideControlsRequest.bind(this)));
    }
    _setHiddenContext(isHidden) {
        this._host.controller.mediaCtx.customControls = !isHidden;
    }
    /**
     * Show all controls.
     *
     * @param request
     */
    async show(request) {
        if (!this.isHidden)
            return;
        this._setHiddenContext(false);
        await this.waitForUpdateComplete();
        this._handleControlsChange(request);
    }
    /**
     * Hide all controls.
     *
     * @param request
     */
    async hide(request) {
        if (this.isHidden)
            return;
        this._setHiddenContext(true);
        await this.waitForUpdateComplete();
        this._handleControlsChange(request);
    }
    /**
     * Wait for all controls `updateComplete` to finish.
     */
    async waitForUpdateComplete() {
        await Promise.all(Array.from(this._managedElements).map((controls) => controls.updateComplete));
    }
    _handleControlsChange(request) {
        if (this.isHidden === this._prevHiddenValue)
            return;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.debugGroup('controls change', !this.isHidden);
        }
        /* c8 ignore stop */
        this._host.dispatchEvent(vdsEvent('vds-controls-change', {
            detail: !this.isHidden,
            originalEvent: request
        }));
        this._prevHiddenValue = this.isHidden;
    }
    async _handleShowControlsRequest(request) {
        request.stopPropagation();
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup(`📬 received \`${request.type}\``)
                .appendWithLabel('Request', request)
                .end();
        }
        /* c8 ignore stop */
        await this.show(request);
    }
    async _handleHideControlsRequest(request) {
        request.stopPropagation();
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup(`📬 received \`${request.type}\``)
                .appendWithLabel('Request', request)
                .end();
        }
        /* c8 ignore stop */
        await this.hide(request);
    }
}
